import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { translations, languageNames, supportedLanguages } from "./translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate unique visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('serava_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('serava_visitor_id', visitorId);
  }
  return visitorId;
};

// Language detection and persistence
const detectLanguage = async () => {
  const savedLang = localStorage.getItem('serava_language');
  if (savedLang && supportedLanguages.includes(savedLang)) {
    return savedLang;
  }
  
  try {
    const response = await axios.get(`${API}/detect-language`);
    const detectedLang = response.data.detected_language;
    if (supportedLanguages.includes(detectedLang)) {
      localStorage.setItem('serava_language', detectedLang);
      return detectedLang;
    }
  } catch (e) {
    console.error('Language detection failed:', e);
  }
  
  return 'en';
};

// SVG Components
const SeravaSVG = () => (
  <svg viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg" className="about-logo-svg">
    <defs>
      <linearGradient id="lavGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#9A8DC4" stopOpacity="0.8"/>
        <stop offset="100%" stopColor="#C4BBDF" stopOpacity="0.3"/>
      </linearGradient>
    </defs>
    <rect x="20" y="20" width="260" height="260" rx="8" fill="none" stroke="url(#lavGrad)" strokeWidth="1"/>
    <text x="150" y="130" textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontSize="48" fill="#FAF8F6" fontWeight="300">SERAVA</text>
    <text x="150" y="175" textAnchor="middle" fontFamily="'DM Mono', monospace" fontSize="16" fill="#9A8DC4" letterSpacing="0.4em">GROUP</text>
    <line x1="60" y1="200" x2="240" y2="200" stroke="#9A8DC4" strokeOpacity="0.3" strokeWidth="1"/>
    <text x="150" y="230" textAnchor="middle" fontFamily="'DM Mono', monospace" fontSize="8" fill="#8A8590" letterSpacing="0.3em">SPORTS · EVENTS · TRAVEL</text>
  </svg>
);

const ManchesterUnitedSVG = () => (
  <svg height="52" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="#DA291C" stroke="#FBE122" strokeWidth="3"/>
    <text x="50" y="44" textAnchor="middle" fontFamily="serif" fontSize="9" fill="#FBE122" fontWeight="bold">MANCHESTER</text>
    <text x="50" y="62" textAnchor="middle" fontFamily="serif" fontSize="14" fill="#FBE122" fontWeight="bold">MU</text>
    <text x="50" y="74" textAnchor="middle" fontFamily="serif" fontSize="9" fill="#FBE122" fontWeight="bold">UNITED</text>
  </svg>
);

const AtleticoSVG = () => (
  <svg height="52" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="#CE3524" stroke="#FFFFFF" strokeWidth="2"/>
    <text x="50" y="48" textAnchor="middle" fontFamily="serif" fontSize="10" fill="#FFFFFF" fontWeight="bold">ATL</text>
    <text x="50" y="64" textAnchor="middle" fontFamily="serif" fontSize="9" fill="#FFFFFF">MADRID</text>
  </svg>
);

const PSGSVG = () => (
  <svg height="52" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="#003F7F" stroke="#DA291C" strokeWidth="3"/>
    <text x="50" y="46" textAnchor="middle" fontFamily="serif" fontSize="14" fill="#FFFFFF" fontWeight="bold">PSG</text>
    <text x="50" y="64" textAnchor="middle" fontFamily="serif" fontSize="8" fill="#DA291C">PARIS SG</text>
  </svg>
);

const BauhausSVG = () => (
  <svg height="38" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="56" height="56" rx="3" fill="#e63030" opacity="0.9"/>
    <text x="30" y="26" textAnchor="middle" fontFamily="'Outfit',sans-serif" fontSize="8" fill="#fff" fontWeight="600">BAUHAUS</text>
    <text x="30" y="40" textAnchor="middle" fontFamily="'Outfit',sans-serif" fontSize="7" fill="#fff">GALAN</text>
  </svg>
);

const ScandsportsSVG = () => (
  <svg width="52" height="52" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="92" height="92" rx="8" fill="#1D1B23" stroke="#9A8DC4" strokeWidth="2"/>
    <text x="50" y="46" textAnchor="middle" fontFamily="'DM Mono',monospace" fontSize="11" fill="#9A8DC4" fontWeight="500">SCAND</text>
    <text x="50" y="63" textAnchor="middle" fontFamily="'DM Mono',monospace" fontSize="11" fill="#9A8DC4" fontWeight="500">SPORTS</text>
  </svg>
);

// Language Selector Component
const LanguageSelector = ({ language, setLanguage }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('serava_language', lang);
    setIsOpen(false);
  };

  return (
    <div className="language-selector" data-testid="language-selector">
      <button 
        className="language-btn" 
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-btn"
      >
        {language.toUpperCase()}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        </svg>
      </button>
      <div className={`language-dropdown ${isOpen ? 'open' : ''}`}>
        {supportedLanguages.map(lang => (
          <button
            key={lang}
            className={`language-option ${lang === language ? 'active' : ''}`}
            onClick={() => handleLanguageChange(lang)}
            data-testid={`lang-option-${lang}`}
          >
            {languageNames[lang]}
          </button>
        ))}
      </div>
    </div>
  );
};

// Main Homepage Component
const HomePage = () => {
  const [language, setLanguage] = useState('en');
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', interest: '', message: '' });
  const [formStatus, setFormStatus] = useState({ submitting: false, success: false, error: false });
  const t = translations[language] || translations.en;

  useEffect(() => {
    const initLanguage = async () => {
      const detectedLang = await detectLanguage();
      setLanguage(detectedLang);
    };
    initLanguage();
  }, []);

  useEffect(() => {
    // Track page view
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/track`, {
          visitor_id: getVisitorId(),
          page: '/',
          event_type: 'pageview',
          language: language
        });
      } catch (e) {
        console.error('Tracking failed:', e);
      }
    };
    trackVisit();
  }, [language]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: false });
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language: language
      });
      setFormStatus({ submitting: false, success: true, error: false });
      setFormData({ name: '', email: '', interest: '', message: '' });
    } catch (err) {
      setFormStatus({ submitting: false, success: false, error: true });
    }
  };

  return (
    <div data-testid="homepage">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`} data-testid="main-nav">
        <a href="#home" className="nav-logo">SERAVA <span>GROUP</span></a>
        <ul className="nav-links">
          <li><a href="#about">{t.nav.about}</a></li>
          <li><a href="#brands">{t.nav.brands}</a></li>
          <li><a href="#events">{t.nav.events}</a></li>
          <li><a href="#team">{t.nav.team}</a></li>
          <li><a href="#contact">{t.nav.contact}</a></li>
        </ul>
        <div className="nav-right">
          <LanguageSelector language={language} setLanguage={setLanguage} />
          <a href="#contact" className="nav-cta" data-testid="nav-cta">{t.nav.getInTouch}</a>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero" id="home" data-testid="hero-section">
        <div className="hero-bg"></div>
        <div className="hero-grid-lines"></div>
        <div className="hero-inner">
          <p className="hero-eyebrow">{t.hero.eyebrow}</p>
          <h1>{t.hero.title}<br/><em>{t.hero.titleHighlight}</em></h1>
          <p className="hero-sub">{t.hero.subtitle}</p>
          <div className="hero-btns">
            <a href="#events" className="btn-primary" data-testid="explore-events-btn">{t.hero.exploreEvents}</a>
            <a href="#about" className="btn-ghost">{t.hero.aboutSerava}</a>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="n">3<span>+</span></div>
              <div className="l">{t.hero.stats.brands}</div>
            </div>
            <div className="hero-stat">
              <div className="n">85<span>M</span></div>
              <div className="l">{t.hero.stats.tvViewers}</div>
            </div>
            <div className="hero-stat">
              <div className="n">80<span>+</span></div>
              <div className="l">{t.hero.stats.ledReach}</div>
            </div>
            <div className="hero-stat">
              <div className="n">12<span>yrs</span></div>
              <div className="l">{t.hero.stats.experience}</div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="about-section" id="about" data-testid="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-visual reveal">
              <SeravaSVG />
              <div className="about-accent"></div>
              <div className="about-accent-2"></div>
            </div>
            <div className="about-text">
              <p className="section-eyebrow reveal">{t.about.eyebrow}</p>
              <h2 className="section-title reveal">{t.about.title} <em>{t.about.titleHighlight}</em></h2>
              <p className="reveal reveal-delay-1">{t.about.p1}</p>
              <p className="reveal reveal-delay-2">{t.about.p2}</p>
              <div className="brand-tags reveal reveal-delay-3">
                {t.about.tags.map((tag, i) => (
                  <span key={i} className="brand-tag">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-line"></div>

      {/* Brands */}
      <section className="brands-section" id="brands" data-testid="brands-section">
        <div className="container">
          <p className="section-eyebrow reveal">{t.brands.eyebrow}</p>
          <h2 className="section-title reveal">{t.brands.title}<br/><em>{t.brands.titleHighlight}</em></h2>
          <p className="section-desc reveal">{t.brands.desc}</p>
          <div className="brands-grid">
            {t.brands.cards.map((card, i) => (
              <div key={i} className={`brand-card reveal reveal-delay-${i + 1}`}>
                <p className="brand-card-num">{card.num}</p>
                <h3 className="brand-card-title">{card.title} <em>{card.titleHighlight}</em></h3>
                <p className="brand-card-body">{card.body}</p>
                <a href="#contact" className="brand-card-link">{card.link}</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="events-section" id="events" data-testid="events-section">
        <div className="container">
          <div className="events-header">
            <div>
              <p className="section-eyebrow reveal">{t.events.eyebrow}</p>
              <h2 className="section-title reveal">{t.events.title}<br/><em>{t.events.titleHighlight}</em></h2>
            </div>
            <p className="section-desc reveal">{t.events.desc}</p>
          </div>
          <div className="events-grid">
            {/* Event 1: Man Utd vs Atletico */}
            <div className="event-card reveal reveal-delay-1">
              <div className="event-card-header">
                <ManchesterUnitedSVG />
                <span className="vs-divider">vs</span>
                <AtleticoSVG />
              </div>
              <div className="event-card-body">
                <p className="event-date">{t.events.cards[0].date}</p>
                <h3 className="event-title">{t.events.cards[0].title}</h3>
                <p className="event-desc">{t.events.cards[0].desc}</p>
                <div className="event-tags">
                  {t.events.cards[0].tags.map((tag, i) => (
                    <span key={i} className={`event-tag ${i === 1 ? 'gold' : ''}`}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Event 2: Man Utd vs PSG */}
            <div className="event-card reveal reveal-delay-2">
              <div className="event-card-header">
                <ManchesterUnitedSVG />
                <span className="vs-divider">vs</span>
                <PSGSVG />
              </div>
              <div className="event-card-body">
                <p className="event-date">{t.events.cards[1].date}</p>
                <h3 className="event-title">{t.events.cards[1].title}</h3>
                <p className="event-desc">{t.events.cards[1].desc}</p>
                <div className="event-tags">
                  {t.events.cards[1].tags.map((tag, i) => (
                    <span key={i} className={`event-tag ${i === 1 ? 'gold' : ''}`}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Event 3: Bauhaus Galan */}
            <div className="event-card reveal reveal-delay-3">
              <div className="event-card-header" style={{ flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <BauhausSVG />
                  <div>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.7rem', letterSpacing: '0.2em', color: '#e63030', fontWeight: 500 }}>BAUHAUS GALAN</div>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.55rem', letterSpacing: '0.15em', color: '#8A8590' }}>7 JUNI 2026</div>
                  </div>
                </div>
              </div>
              <div className="event-card-body">
                <p className="event-date">{t.events.cards[2].date}</p>
                <h3 className="event-title">{t.events.cards[2].title}</h3>
                <p className="event-desc">{t.events.cards[2].desc}</p>
                <div className="event-tags">
                  {t.events.cards[2].tags.map((tag, i) => (
                    <span key={i} className={`event-tag ${i === 1 ? 'gold' : ''}`}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="partners-section" data-testid="partners-section">
        <div className="container">
          <p className="section-eyebrow reveal" style={{ justifyContent: 'center', marginBottom: '0.5rem' }}>{t.partners.eyebrow}</p>
          <h2 className="section-title reveal" style={{ textAlign: 'center' }}>
            {t.partners.title}<br/><em>{t.partners.titleHighlight}</em>
          </h2>
          <div className="partners-row">
            <div className="partner-item reveal reveal-delay-1">
              <ManchesterUnitedSVG />
              <span className="partner-name">{t.partners.names[0]}</span>
            </div>
            <div className="partner-item reveal reveal-delay-2">
              <ScandsportsSVG />
              <span className="partner-name">{t.partners.names[1]}</span>
            </div>
            <div className="partner-item reveal reveal-delay-3">
              <AtleticoSVG />
              <span className="partner-name">{t.partners.names[2]}</span>
            </div>
            <div className="partner-item reveal reveal-delay-4">
              <PSGSVG />
              <span className="partner-name">{t.partners.names[3]}</span>
            </div>
            <div className="partner-item reveal reveal-delay-4">
              <svg width="52" height="52" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="96" height="96" rx="6" fill="#e63030"/>
                <text x="50" y="43" textAnchor="middle" fontFamily="'Outfit',sans-serif" fontSize="12" fill="#fff" fontWeight="700">BAUHAUS</text>
                <text x="50" y="62" textAnchor="middle" fontFamily="'Outfit',sans-serif" fontSize="11" fill="#fff">GALAN</text>
              </svg>
              <span className="partner-name">{t.partners.names[4]}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="team-section" id="team" data-testid="team-section">
        <div className="container">
          <p className="section-eyebrow reveal">{t.team.eyebrow}</p>
          <h2 className="section-title reveal">{t.team.title}<br/><em>{t.team.titleHighlight}</em></h2>
          <p className="section-desc reveal">{t.team.desc}</p>
          <div className="team-grid">
            {t.team.members.map((member, i) => (
              <div key={i} className={`team-card reveal reveal-delay-${i + 1}`}>
                <div className="team-initials">{member.initials}</div>
                <h3 className="team-name">{member.name}</h3>
                <p className="team-role">{member.role}</p>
                <p className="team-bio">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider-line"></div>

      {/* Contact */}
      <section className="contact-section" id="contact" data-testid="contact-section">
        <div className="container">
          <div className="contact-inner">
            <div className="contact-left reveal">
              <p className="section-eyebrow">{t.contact.eyebrow}</p>
              <h2 className="section-title">{t.contact.title}<br/><em>{t.contact.titleHighlight}</em></h2>
              <p>{t.contact.p1}</p>
              <div className="contact-detail">
                <div className="contact-item">
                  <span className="contact-item-label">{t.contact.details.email}</span>
                  <span className="contact-item-val">
                    <a href="mailto:contact@seravagroup.com">contact@seravagroup.com</a>
                  </span>
                </div>
                <div className="contact-item">
                  <span className="contact-item-label">{t.contact.details.phone}</span>
                  <span className="contact-item-val">+46 70 741 51 21</span>
                </div>
                <div className="contact-item">
                  <span className="contact-item-label">{t.contact.details.hq}</span>
                  <span className="contact-item-val">Stockholm, Sweden</span>
                </div>
                <div className="contact-item">
                  <span className="contact-item-label">{t.contact.details.web}</span>
                  <span className="contact-item-val">
                    <a href="#">www.seravagroup.com</a>
                  </span>
                </div>
              </div>
            </div>
            <form className="contact-form reveal reveal-delay-2" onSubmit={handleSubmit} data-testid="contact-form">
              <div className="form-group">
                <label className="form-label">{t.contact.form.name}</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder={t.contact.form.namePlaceholder}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="input-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t.contact.form.email}</label>
                <input 
                  type="email" 
                  className="form-input" 
                  placeholder={t.contact.form.emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="input-email"
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t.contact.form.interest}</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder={t.contact.form.interestPlaceholder}
                  value={formData.interest}
                  onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                  required
                  data-testid="input-interest"
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t.contact.form.message}</label>
                <textarea 
                  className="form-input form-textarea" 
                  placeholder={t.contact.form.messagePlaceholder}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  data-testid="input-message"
                />
              </div>
              <button 
                type="submit" 
                className="form-submit" 
                disabled={formStatus.submitting}
                data-testid="submit-btn"
              >
                {formStatus.submitting ? '...' : t.contact.form.submit}
              </button>
              {formStatus.success && (
                <div className="form-message success" data-testid="form-success">{t.contact.form.success}</div>
              )}
              {formStatus.error && (
                <div className="form-message error" data-testid="form-error">{t.contact.form.error}</div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer">
        <span className="footer-logo">SERAVA <span>GROUP</span></span>
        <ul className="footer-links">
          <li><a href="#about">{t.nav.about}</a></li>
          <li><a href="#brands">{t.nav.brands}</a></li>
          <li><a href="#events">{t.nav.events}</a></li>
          <li><a href="#team">{t.nav.team}</a></li>
          <li><a href="#contact">{t.nav.contact}</a></li>
        </ul>
        <span className="footer-copy">{t.footer.copy}</span>
      </footer>
    </div>
  );
};

// Admin Page Component
const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedToken = localStorage.getItem('serava_admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchStats();
    }
  }, [isLoggedIn, token]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/stats?token=${token}`);
      setStats(response.data);
    } catch (e) {
      if (e.response?.status === 401) {
        handleLogout();
      }
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      if (response.data.success) {
        setToken(response.data.token);
        localStorage.setItem('serava_admin_token', response.data.token);
        setIsLoggedIn(true);
      }
    } catch (e) {
      setLoginError('Invalid credentials');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken('');
    localStorage.removeItem('serava_admin_token');
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login" data-testid="admin-login">
        <div className="admin-login-card">
          <h1>Admin <span style={{ color: 'var(--lavender)' }}>Portal</span></h1>
          <p>Serava Group Statistics Dashboard</p>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input 
                type="email" 
                className="form-input"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                data-testid="admin-email"
              />
            </div>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Password</label>
              <input 
                type="password" 
                className="form-input"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                data-testid="admin-password"
              />
            </div>
            {loginError && (
              <div className="form-message error" style={{ marginTop: '1rem' }}>{loginError}</div>
            )}
            <button type="submit" className="form-submit" style={{ marginTop: '1.5rem', width: '100%' }} data-testid="admin-login-btn">
              Login
            </button>
          </form>
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <a href="/" style={{ color: 'var(--lavender)', fontSize: '0.8rem', textDecoration: 'none' }}>← Back to website</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="admin-header">
        <h1>Serava <span>Admin</span></h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button onClick={fetchStats} className="admin-logout" style={{ opacity: loading ? 0.5 : 1 }}>
            Refresh
          </button>
          <button onClick={handleLogout} className="admin-logout" data-testid="admin-logout">
            Logout
          </button>
        </div>
      </div>
      <div className="admin-content">
        {stats ? (
          <>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-card-label">Total Visitors</div>
                <div className="stat-card-value" data-testid="stat-total-visitors">{stats.total_visitors}</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-label">Unique IPs</div>
                <div className="stat-card-value">{stats.unique_visitors}</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-label">Page Views</div>
                <div className="stat-card-value">{stats.total_pageviews}</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-label">Form Submissions</div>
                <div className="stat-card-value" data-testid="stat-submissions">{stats.total_submissions}</div>
              </div>
            </div>

            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div className="stat-card">
                <div className="stat-card-label">Today</div>
                <div className="stat-card-value">{stats.visitors_today} <span style={{ fontSize: '1rem', color: 'var(--mid-grey)' }}>visitors</span></div>
              </div>
              <div className="stat-card">
                <div className="stat-card-label">This Week</div>
                <div className="stat-card-value">{stats.visitors_week} <span style={{ fontSize: '1rem', color: 'var(--mid-grey)' }}>visitors</span></div>
              </div>
              <div className="stat-card">
                <div className="stat-card-label">This Month</div>
                <div className="stat-card-value">{stats.visitors_month} <span style={{ fontSize: '1rem', color: 'var(--mid-grey)' }}>visitors</span></div>
              </div>
            </div>

            <div className="admin-grid">
              <div className="admin-section">
                <h3 className="admin-section-title">Top Countries</h3>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Visitors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_countries.length > 0 ? (
                      stats.top_countries.map((item, i) => (
                        <tr key={i}>
                          <td>{item.country}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="2" style={{ textAlign: 'center', opacity: 0.5 }}>No data yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="admin-section">
                <h3 className="admin-section-title">Top Languages</h3>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Language</th>
                      <th>Visitors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_languages.length > 0 ? (
                      stats.top_languages.map((item, i) => (
                        <tr key={i}>
                          <td>{item.language.toUpperCase()}</td>
                          <td>{item.count}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="2" style={{ textAlign: 'center', opacity: 0.5 }}>No data yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="admin-section">
              <h3 className="admin-section-title">Recent Contact Submissions</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Interest</th>
                    <th>Language</th>
                    <th>Email Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_submissions.length > 0 ? (
                    stats.recent_submissions.map((sub, i) => (
                      <tr key={i}>
                        <td>{new Date(sub.created_at).toLocaleDateString()}</td>
                        <td>{sub.name}</td>
                        <td>{sub.email}</td>
                        <td>{sub.interest}</td>
                        <td>{sub.language?.toUpperCase()}</td>
                        <td style={{ color: sub.email_sent ? 'var(--lavender)' : 'var(--red)' }}>
                          {sub.email_sent ? 'Yes' : 'No'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="6" style={{ textAlign: 'center', opacity: 0.5 }}>No submissions yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--mid-grey)' }}>
            Loading statistics...
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
